/**
 * THE SUPPORT SURFACE'S WORDS. Since design 29 (W5) the words themselves are the catalogue's
 * `support` namespace (`src/i18n/locales/<lang>/support.json`), in fourteen languages; this file
 * keeps what is not words - the guides' keys and destinations, the answers' order - and the two
 * tables other screens and specs read (`SUPPORT_COPY`, `TERMS_COPY`), which translate on every read.
 *
 * WHY SO MUCH OF THIS PAGE IS STATIC AT ALL. LOCKED D37 (`docs/design/00-scope-v1.md`):
 * "RULED 2026-08-21: both deferred — e-mail is enough for v1 … v1: support/editorial e-mail
 * addresses surfaced in the cabinet; the S2-migrated FAQ still ships as static pages." And erratum
 * G28 (`00-scope-v1.md`) puts the program / FAQ / terms pages on moonx.app: "the AUTHORS app
 * hosts only the authenticated surfaces". So the cabinet's Support page is a page of link-outs
 * plus one contact block. Exactly TWO things on it are data:
 *
 *   1. the two contact addresses      — `getPlatformContacts` (`authors-api.yaml`)
 *   2. the live terms record          — `getCurrentTerms`     (`authors-api.yaml`)
 *
 * Everything else in this file is copy.
 *
 * PROVENANCE OF THE FIVE CARDS AND THREE ANSWERS. They are the owner's own prototype's, verbatim
 * except where noted below: `moonx-authors-prototype/MoonX Pages.dc.html`
 * (`SUPPORT_DOCS`, `SUPPORT_FAQ`) and the rendered view at :3050-3087. The prototype is INTENT,
 * not spec (decision-log row 117), and layout/wording are ours (row 99) — so the two places the
 * copy could mislead an author have been rewritten rather than transcribed, and each says why.
 */

import { ENGLISH_RESOURCES, i18n } from '../../i18n';
import { liveTexts } from '../cabinet/live-texts';

type SupportTexts = (typeof ENGLISH_RESOURCES)['support'];

/** One guide the cabinet links out to. The document itself is rendered by moonx.app, never by us. */
export interface SupportDoc {
  /**
   * Stable key: React's, the terms binding's below, and the catalogue's (`support:docs.<key>`
   * holds the card's title and summary). Never shown.
   */
  readonly key: keyof SupportTexts['docs'];
  /**
   * Where the card goes.
   *
   * `null` means NO DESTINATION IS KNOWN, and a card with `null` renders as a titled row that does
   * not pretend to be a link — the same discipline the contract applies to a missing address. Since
   * the launch the three guides have one (ruling N15 of `docs/design/27-launch-texts/01-decisions.md`:
   * `https://moonx.app/for-authors/<slug>`); the agreement's card takes its URL at runtime.
   *
   * They were filled in for the launch (A5-GUIDES; three since the website's word, 2026-10-12: no
   * monthly-reports page), here and not in `platform_settings`, being URLs on a site we do not render; the settings
   * singleton was justified as three typed operational fields.
   */
  readonly href: string | null;
}

/**
 * THE TERMS CARD IS ONE OF THE FIVE, AND THIS KEY IS HOW IT IS FOUND.
 *
 * `TermsState` carries NO `documentCode` and no label (`authors-api.yaml`), so the
 * cabinet cannot learn WHICH document `contentUrl` points at. At v1 there is exactly one:
 * `terms_document` seeds a single row, `('creator_program', 'Creator programme terms', …)`
 * (`04-schema.md`). Binding the live URL to the guidelines card is therefore an
 * INFERENCE from "there is only one document", not something the corpus states. It is recorded
 * here rather than buried in a component so it can be corrected in one line if a second document
 * ever exists.
 */
export const TERMS_DOC_KEY = 'creators-program-guidelines';

export const SUPPORT_DOCS: readonly SupportDoc[] = [
  // Deliberately no `href`: this card's destination arrives as `TermsState.contentUrl`.
  { key: TERMS_DOC_KEY, href: null },
  { key: 'how-review-works', href: 'https://moonx.app/for-authors/how-review-works' },
  { key: 'how-payments-work', href: 'https://moonx.app/for-authors/how-payments-work' },
  { key: 'community-rules', href: 'https://moonx.app/for-authors/community-rules' },
  // v1.0.29: what a video may show, and the eight rules an author confirms when sending one.
  { key: 'video-guidelines', href: 'https://moonx.app/for-authors/video-guidelines' },
];

/** One quick answer: its question and answer are `support:answers.<key>`. */
export interface QuickAnswer {
  readonly key: keyof SupportTexts['answers'];
}

/*
 * WHY THE THREE ANSWERS SAY WHAT THEY SAY. They are the owner's prototype's
 * (`moonx-authors-prototype/MoonX Pages.dc.html`, `SUPPORT_FAQ`), the prototype being intent and not
 * spec (decision-log row 117), with two rewritten:
 *   - `review-time`: the prototype said "two to three days"; the platform's commitment is a
 *     3-WORKING-DAY SLA (`01-system-overview.md`), and calendar days are a promise nobody signed.
 *   - `when-paid`: the prototype named three live policy values (the 10th, the 15th, $15). The
 *     threshold is versioned policy the Payments view already reads, and the run day is on no
 *     author-facing operation at all, so the answer describes the SHAPE and sends the author to the
 *     page that holds their real numbers - and says no internal words (design 15).
 */
export const QUICK_ANSWERS: readonly QuickAnswer[] = [
  { key: 'review-time' },
  { key: 'returned-article' },
  { key: 'when-paid' },
];

/*
 * NOTES ON THE WORDING, kept here because the catalogue is JSON and has no comments.
 *   - `termsLoading`, `contactLoading` and `TERMS_COPY.loading` are NOUN PHRASES: the shared
 *     LoadingState announces them as "Loading {label}."
 *   - `termsBlocksBody` is THE ONE SENTENCE ON THE PAGE THAT COULD FRIGHTEN SOMEBODY ABOUT MONEY. It
 *     is said of the SUBMISSION documents, which hold back sending an article for review and never a
 *     payout (`terms_stale` is not a payout block reason; design 25's words). The terms screen's
 *     `blockedNoticeBody` says it word for word.
 *   - The contact block offers two e-mail addresses, not the prototype's "Chats": Chats and support
 *     tickets are deferred (LOCKED D37). Both addresses are NULL until an operator sets them, a real
 *     shipped state, so "none published yet" is said, never a placeholder address (`authors-api.yaml`).
 *   - A 403 on the contacts is not the author's fault and the corpus names no reason, so
 *     `contactForbidden*` asserts none.
 *   - `TERMS_COPY`: `supersededTitle` is the accept's 404 (a newer version was published while the
 *     screen sat open); `throttledBody` is for a 429 that names no wait, one that names it reads the
 *     app's one sentence (`tooManyRequests`, features/cabinet/api/problem.ts).
 */

/** Everything on the support page that is not a card or an answer, in the language of each read. */
export const SUPPORT_COPY = liveTexts(
  Object.keys(ENGLISH_RESOURCES.support.page) as (keyof SupportTexts['page'])[],
  (key) => i18n.t(`page.${key}`, { ns: 'support' }),
);

/**
 * The interstitial at `/terms`, in the language of each read. One screen: every owed submission
 * document, one box each, and "Accept and continue" (design 25).
 */
export const TERMS_COPY = liveTexts(
  Object.keys(ENGLISH_RESOURCES.support.terms) as (keyof SupportTexts['terms'])[],
  (key) => i18n.t(`terms.${key}`, { ns: 'support' }),
);
